'use client';

import React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import {
  Search,
  BookOpen,
  PenTool,
  User,
  Coins,
  Settings,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useUserStore } from '@/utils/util_states';
import { localizedPublicPath, stripPublicLocale } from '@/i18n/config';
import { getLanguageDataByCode } from '@/i18n/language-data';

// ─────────────────────────────────────────────
// Navigation items (same routes as full sidebar)
// ─────────────────────────────────────────────
type MiniNavItem = {
  route: string;
  icon: React.ComponentType<{ className?: string }>;
  tooltip: string;
};

// NAV_ITEMS is computed inside the component to access langData

// ─────────────────────────────────────────────
// Mini Sidebar Component (68px)
// ─────────────────────────────────────────────
export default function SidebarMini({ languageCode }: { languageCode: string }) {
  const pathname = usePathname();
  const basePathname = stripPublicLocale(pathname);
  const { userProfile, isLogin } = useUserStore();
  const langData = getLanguageDataByCode(languageCode);
  const discoverRoute = localizedPublicPath('/discover', languageCode);

  const navItems: MiniNavItem[] = [
    { route: discoverRoute, icon: Search, tooltip: langData.nav_discover },
    { route: '/puzzles', icon: BookOpen, tooltip: langData.nav_my_puzzles },
    { route: '/create', icon: PenTool, tooltip: langData.nav_create },
    { route: '/profile', icon: User, tooltip: langData.nav_profile },
    { route: '/recharge', icon: Coins, tooltip: langData.nav_recharge },
    { route: '/settings', icon: Settings, tooltip: langData.nav_settings },
  ];

  return (
    <aside
      className={cn(
        'fixed left-0 top-0 z-40 flex h-screen w-[68px] flex-col items-center',
        'border-r border-[var(--sidebar-border)]',
        'bg-[var(--sidebar)]',
        'animate-[fadeIn_0.3s_ease-out]',
      )}
    >
      {/* ── Logo ── */}
      <Link
        href={discoverRoute}
        className="flex h-[56px] w-full items-center justify-center pt-1"
      >
        <span className="text-[16px]">🎩</span>
      </Link>

      {/* ── Divider ── */}
      <div className="mx-3 mb-2 h-px w-[calc(100%-24px)] bg-[var(--sidebar-border)]" />

      {/* ── Navigation ── */}
      <nav className="flex flex-1 flex-col items-center gap-1 px-2">
        {navItems.map((item) => {
          const itemBaseRoute = stripPublicLocale(item.route);
          const isActive = basePathname === itemBaseRoute || basePathname.startsWith(itemBaseRoute + '/');
          const Icon = item.icon;

          return (
            <Link
              key={item.route}
              href={item.route}
              title={item.tooltip}
              className={cn(
                'group relative flex h-[44px] w-[44px] items-center justify-center rounded-sm transition-all duration-200',
                isActive
                  ? 'bg-[var(--lamp-dim)] text-[var(--lamp)]'
                  : 'text-gray-500 hover:text-gray-300 hover:bg-[var(--sidebar-accent)]',
              )}
            >
              {/* Left accent bar */}
              {isActive && (
                <span className="absolute left-0 top-1/2 h-[60%] w-[2px] -translate-y-1/2 rounded-full bg-[var(--lamp)]" />
              )}

              <Icon className="h-[20px] w-[20px]" />

              {/* Tooltip on hover */}
              <span
                className={cn(
                  'pointer-events-none absolute left-[56px] whitespace-nowrap rounded-sm px-2 py-1',
                  'bg-[var(--popover)] text-[var(--popover-foreground)]',
                  'text-[11px] shadow-lg',
                  'opacity-0 transition-opacity group-hover:opacity-100',
                  'border border-[var(--border)]',
                )}
                style={{ fontFamily: 'var(--font-noto), "Noto Sans SC", sans-serif' }}
              >
                {item.tooltip}
              </span>
            </Link>
          );
        })}
      </nav>

      {/* ── Footer: user avatar ── */}
      <div className="pb-4 pt-2">
        <Link href="/profile" title={isLogin ? userProfile.username || 'Profile' : langData.profile_not_logged_in}>
          <div
            className={cn(
              'flex h-[36px] w-[36px] items-center justify-center rounded-full',
              'bg-[var(--lamp-dim)] text-[var(--lamp)]',
              'text-[12px] font-medium transition-colors',
              'hover:bg-[var(--lamp-glow)]',
            )}
            style={{ fontFamily: 'var(--font-playfair), "Playfair Display", serif' }}
          >
            {isLogin && userProfile.avatar ? (
              <img
                src={userProfile.avatar}
                alt="avatar"
                className="h-full w-full rounded-full object-cover"
              />
            ) : isLogin && userProfile.username ? (
              userProfile.username.charAt(0).toUpperCase()
            ) : (
              <User className="h-4 w-4" />
            )}
          </div>
        </Link>
      </div>
    </aside>
  );
}
